/**
 * Reconstruct the 295-cell board from a replay's initial_setup +
 * bundled geometry/tile JSON. Vite inlines the JSON at build time.
 *
 * Runtime is deterministic: given the same board_assignments and same
 * static JSON, the resulting cells are identical to what the engine
 * produced during simulation.
 */

import geometryJson from "../../../spec/game-spec/data/board_geometry.json";
import tileFacesJson from "../../../spec/game-spec/data/tile_faces.provisional.json";
import { hexKey, rotate60 } from "./hex.js";
import type { Hex, PlateAssignment } from "./types.js";

export type Terrain = "grass" | "beach" | "water" | "sand" | "rock" | "swamp";
export type SiteKind = "crate" | "landmark";
export type CellKind = "plate" | "cloth_water";

export interface BoardCell {
  key: string;
  q: number;
  r: number;
  kind: CellKind;
  terrain: Terrain;
  site: SiteKind | null;
  waterLayer?: 1 | 2 | 3;
  isOuterBoundary?: boolean;
  plateSlotId?: string;
  tileId?: number;
  face?: "A" | "B";
  rotation?: number;
  faceCellIndex?: number;
}

export interface Board {
  cells: Map<string, BoardCell>;
  crateSites: Hex[];
  landmarkSites: Hex[];
  outerBoundary: Hex[];
}

interface RawGeometry {
  plate_slots: { slot_id: string; center: Hex; global_cells: Hex[] }[];
  cloth_water: {
    cells: (Hex & { layer: 1 | 2 | 3 })[];
    outer_boundary_cells: Hex[];
  };
}

interface RawFace {
  tile_id: number;
  face: "A" | "B";
  cells: {
    index: number;
    q: number;
    r: number;
    terrain: Terrain;
    site: SiteKind | null;
  }[];
}

const GEOM = geometryJson as unknown as RawGeometry;
const FACES = (tileFacesJson as unknown as { faces: RawFace[] }).faces;

export function buildBoardFromAssignments(
  assignments: PlateAssignment[],
): Board {
  const slotBy = new Map<string, RawGeometry["plate_slots"][number]>();
  for (const s of GEOM.plate_slots) slotBy.set(s.slot_id, s);

  const cells = new Map<string, BoardCell>();
  for (const a of assignments) {
    const slot = slotBy.get(a.slot_id);
    if (!slot) throw new Error(`boardBuild: unknown slot ${a.slot_id}`);
    const face = FACES.find((f) => f.tile_id === a.tile_id && f.face === a.face);
    if (!face) {
      throw new Error(
        `boardBuild: no face for tile ${a.tile_id}/${a.face}`,
      );
    }
    for (const c of face.cells) {
      const rotated = rotate60({ q: c.q, r: c.r }, a.rotation);
      const global: Hex = {
        q: rotated.q + slot.center.q,
        r: rotated.r + slot.center.r,
      };
      const key = hexKey(global);
      cells.set(key, {
        key,
        q: global.q,
        r: global.r,
        kind: "plate",
        terrain: c.terrain,
        site: c.site,
        plateSlotId: slot.slot_id,
        tileId: a.tile_id,
        face: a.face,
        rotation: a.rotation,
        faceCellIndex: c.index,
      });
    }
  }

  const boundarySet = new Set(
    GEOM.cloth_water.outer_boundary_cells.map((c) => hexKey(c)),
  );
  for (const w of GEOM.cloth_water.cells) {
    const key = hexKey(w);
    cells.set(key, {
      key,
      q: w.q,
      r: w.r,
      kind: "cloth_water",
      terrain: "water",
      site: null,
      waterLayer: w.layer,
      isOuterBoundary: boundarySet.has(key),
    });
  }

  const crateSites: Hex[] = [];
  const landmarkSites: Hex[] = [];
  for (const c of cells.values()) {
    if (c.site === "crate") crateSites.push({ q: c.q, r: c.r });
    if (c.site === "landmark") landmarkSites.push({ q: c.q, r: c.r });
  }

  return {
    cells,
    crateSites,
    landmarkSites,
    outerBoundary: GEOM.cloth_water.outer_boundary_cells,
  };
}
